import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import EventRepeatOutlined from '@mui/icons-material/EventRepeatOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { useSession as useAuthSession } from '../../../lib/authClient.js';
import {
  useAutomationDraft,
  useAutomationForSession,
  useCreateAutomation,
} from '../../../lib/automations.hooks.js';
import { RunCreatedToast } from '../RunCreatedToast.js';
import {
  AutomationDrawer,
  type AutomationSubmit,
  defaultForm,
  type Save,
} from './AutomationDrawer.js';
import { dayLabel, runAtLabel, scheduleLabel } from './automationText.js';

const ICON_SIZE = 36;

interface AutomateCardProps {
  // The session's root run; null until the session has completed.
  sessionId: string | null;
  accountCount: number;
  onOpenAutomations: () => void;
}

interface Saved {
  name: string;
  nextRunAt: string | Date | null;
}

function CardButton({
  title,
  line,
  onClick,
}: {
  title: string;
  line: string;
  onClick: () => void;
}): ReactNode {
  return (
    <ButtonBase
      onClick={onClick}
      data-testid="automate-card"
      sx={{
        display: 'flex',
        gap: 2,
        px: 3,
        py: 2.5,
        textAlign: 'left',
        alignItems: 'center',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'primary.light',
        bgcolor: 'background.paper',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: ICON_SIZE,
          height: ICON_SIZE,
          flexShrink: 0,
          borderRadius: 1.5,
          bgcolor: 'primary.light',
          color: 'primary.main',
        }}
      >
        <EventRepeatOutlined sx={{ fontSize: 20 }} />
      </Box>
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{line}</Typography>
      </Stack>
      <ArrowForwardOutlined sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
    </ButtonBase>
  );
}

export function AutomateCard({
  sessionId,
  accountCount,
  onOpenAutomations: openAutomations,
}: AutomateCardProps): ReactNode {
  const email = useAuthSession().data?.user.email ?? null;
  const draft = useAutomationDraft(sessionId);
  const existing = useAutomationForSession(sessionId);
  const create = useCreateAutomation();
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(0);
  const [notice, setNotice] = useState<ReactNode>(null);
  const [toast, setToast] = useState<Saved | null>(null);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);

  if (draft.data?.automatable !== true || existing.data === undefined) return null;
  const { steps, name, windowDays } = draft.data;
  const scopeLine = `${accountCount} ${accountCount === 1 ? 'account' : 'accounts'} · last ${windowDays} days`;
  const automation = existing.data;

  // A duplicate warning arms the next save to go through, with whatever the form then holds.
  const handleSave = (submit: AutomationSubmit, then: Save): void => {
    setNotice(null);
    create.mutate(
      { sessionId: sessionId ?? '', ...submit, confirmDuplicate },
      {
        onSuccess: (result) => {
          if (result.status === 'duplicate') {
            setConfirmDuplicate(true);
            setNotice(
              <Alert severity="warning">
                An automation with the same steps, accounts and frequency already exists. Save again
                to create this one anyway.
              </Alert>,
            );
            return;
          }
          if (result.status === 'cap_reached') {
            setNotice(
              <Alert severity="error">
                Your organization has reached its limit of active automations. Pause or delete one
                first{result.active.length === 0 ? '.' : ':'}{' '}
                {result.active.map((row) => row.name).join(', ')}
              </Alert>,
            );
            return;
          }
          setOpen(false);
          if (then === 'view') return openAutomations();
          setToast({ name: submit.schedule.name, nextRunAt: result.nextRunAt });
        },
        onError: (error) =>
          setNotice(
            <Alert severity="error">
              {error.message === 'schedule_ended'
                ? 'This schedule has no runs left. Pick a later end date.'
                : 'The automation could not be saved.'}
            </Alert>,
          ),
      },
    );
  };

  return (
    <>
      <Divider />
      <Stack sx={{ gap: 3 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>
          {automation === null ? 'AUTOMATE' : 'AUTOMATION'}
        </Typography>
        {automation === null ? (
          <CardButton
            title="Save as automation"
            line="Re-run this session on a schedule"
            onClick={() => {
              setNotice(null);
              setConfirmDuplicate(false);
              setOpened(opened + 1);
              setOpen(true);
            }}
          />
        ) : (
          <CardButton
            title={automation.name}
            line={
              automation.nextRunAt === null
                ? scheduleLabel(automation.frequency, automation.timeOfDay)
                : `${scheduleLabel(automation.frequency, automation.timeOfDay)} · next run ${dayLabel(automation.nextRunAt)}`
            }
            onClick={openAutomations}
          />
        )}
      </Stack>

      <AutomationDrawer
        key={opened}
        open={open}
        mode="create"
        steps={steps}
        scopeLine={scopeLine}
        initial={defaultForm(`${name} weekly`, email)}
        saving={create.isPending}
        notice={notice}
        onSave={handleSave}
        onClose={() => setOpen(false)}
      />

      <RunCreatedToast
        open={toast !== null}
        actionName=""
        outcomeKind={null}
        titleText="Automation saved"
        detail={
          toast === null
            ? null
            : `${toast.name}${toast.nextRunAt === null ? '' : ` · next run ${runAtLabel(toast.nextRunAt)}`}`
        }
        viewLabel="View automations"
        onView={openAutomations}
        onClose={() => setToast(null)}
      />
    </>
  );
}
